import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorSchema = z.object({ message: z.string() });

const regionIdSchema = z.string().regex(/^reg_[0-9A-Za-z]{22}$/);
const threadIdSchema = z.string().min(1).max(64);

export const regionStatusValues = ['active', 'quiet', 'dormant', 'archived'] as const;
export const regionStatusSchema = z.enum(regionStatusValues);

export const regionAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  // Browser-fetchable presigned avatar URL when the user has uploaded
  // a profile picture; null when they haven't (clients fall back to the
  // colour-initial chip).
  avatar_url: z.string().nullable().optional(),
});

export const regionContributorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  thread_count: z.number().int().positive(),
});

export const regionDecisionKindValues = [
  'asked',
  'requested',
  'decided',
  'corrected',
  'shared',
  'requirement',
] as const;
export const regionDecisionKindSchema = z.enum(regionDecisionKindValues);

export const regionDecisionSchema = z.object({
  id: z.string().min(1),
  kind: regionDecisionKindSchema,
  summary: z.string(),
  thread_id: z.string().min(1),
  thread_title: z.string(),
  created_at: z.string().datetime(),
});

export const regionVelocitySchema = z.object({
  decided_14d: z.number().int().nonnegative(),
  asked_14d: z.number().int().nonnegative(),
});

export const regionCitedBySchema = z.object({
  id: regionIdSchema,
  name: z.string().min(1),
  overlap_score: z.number().min(0).max(1),
});

export const regionThreadSchema = z.object({
  thread_id: z.string().min(1),
  title: z.string().min(1),
  is_primary: z.boolean(),
  added_at: z.string().datetime(),
  added_by: regionAuthorSchema,
});

export const regionListObjectSchema = z.object({
  id: regionIdSchema,
  name: z.string().min(1),
  blurb: z.string().nullable(),
  status: regionStatusSchema,
  thread_count: z.number().int().nonnegative(),
  contributor_count: z.number().int().nonnegative(),
  comment_count: z.number().int().nonnegative(),
  /**
   * Top three contributors by thread count within the region. Used
   * for the avatar stack on the region card.
   */
  contributors_preview: z.array(regionAuthorSchema).max(3),
  /**
   * Most-frequent directory prefix across the region's threads (e.g.
   * `apps/api/src/oauth`). `null` when no thread in the region has a
   * recognisable filepath. Rendered as a file chip in the card footer.
   */
  top_filepath: z.string().nullable(),
  last_active_at: z.string().datetime(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});

export const regionListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('region'),
  objects: z.array(regionListObjectSchema),
});

export const listRegionsQuerySchema = z.object({
  status: regionStatusSchema.optional(),
});

export const createRegionRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  blurb: z.string().trim().max(2000).optional(),
});

export const createRegionResponseSchema = z.object({
  id: regionIdSchema,
  created_at: z.string().datetime(),
});

export const updateRegionRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    blurb: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be provided',
  });

export const updateRegionResponseSchema = z.object({
  id: regionIdSchema,
  updated_at: z.string().datetime(),
});

export const archiveRegionRequestSchema = z.object({
  archived: z.boolean(),
});

export const archiveRegionResponseSchema = z.object({
  id: regionIdSchema,
  status: regionStatusSchema,
  archived_at: z.string().datetime().nullable(),
});

export const getRegionResponseSchema = z.object({
  id: regionIdSchema,
  name: z.string().min(1),
  blurb: z.string().nullable(),
  status: regionStatusSchema,
  created_by: regionAuthorSchema,
  thread_count: z.number().int().nonnegative(),
  last_active_at: z.string().datetime(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
  synthesized_status: z.string().nullable(),
  synthesized_status_at: z.string().datetime().nullable(),
  threads: z.array(regionThreadSchema),
  contributors: z.array(regionContributorSchema),
  decisions: z.object({
    decided: z.array(regionDecisionSchema),
    open: z.array(regionDecisionSchema),
  }),
  velocity: regionVelocitySchema,
  /**
   * Active regions whose decisions textually overlap with this
   * region's decisions. Populated for dormant regions so the page
   * reads "this region's decisions still feed live work".
   */
  cited_by_active_regions: z.array(regionCitedBySchema),
});

export const synthesizeRegionResponseSchema = z.object({
  id: regionIdSchema,
  synthesized_status: z.string(),
  synthesized_status_at: z.string().datetime(),
});

// Refresh response from the single clustering-to-regions endpoint.
// `regions_created` counts the rows landed by the synchronous
// deterministic pass; `llm_dispatched` signals whether the async LLM
// pass was kicked off (UI uses this to decide whether to keep polling
// /regions to catch its output).
export const refreshRegionsResponseSchema = z.object({
  regions_created: z.number().int().nonnegative(),
  llm_dispatched: z.boolean(),
});

export const transitionRegionLifecycleResponseSchema = z.object({
  promoted_to_active: z.number().int().nonnegative(),
  demoted_to_quiet: z.number().int().nonnegative(),
  demoted_to_dormant: z.number().int().nonnegative(),
});

export const regionSuggestionSchema = z.object({
  thread_id: z.string().min(1),
  region_id: regionIdSchema,
  region_name: z.string().min(1),
  score: z.number().min(0).max(1),
  signal: z.enum(['file', 'embedding', 'combined']),
});

export const suggestRegionsForThreadsRequestSchema = z.object({
  thread_ids: z.array(z.string().min(1)).min(1).max(50),
});

export const suggestRegionsForThreadsResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('region_suggestion'),
  objects: z.array(regionSuggestionSchema),
});

export const unfiledThreadSchema = z.object({
  thread_id: z.string().min(1),
  title: z.string(),
  created_at: z.string().datetime(),
  author: regionAuthorSchema,
  user_message_count: z.number().int().nonnegative(),
  comment_count: z.number().int().nonnegative(),
});

export const listUnfiledThreadsResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('unfiled_thread'),
  objects: z.array(unfiledThreadSchema),
});

export const addRegionThreadRequestSchema = z.object({
  thread_id: threadIdSchema,
});

export const addRegionThreadResponseSchema = regionThreadSchema;

export const regionsContract = c.router({
  listRegions: {
    method: 'GET',
    path: '/regions',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listRegionsQuerySchema,
    responses: {
      200: regionListResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary: 'List regions in the authenticated user’s organization',
  },
  createRegion: {
    method: 'POST',
    path: '/regions',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createRegionRequestSchema,
    responses: {
      201: createRegionResponseSchema,
      401: errorSchema,
      403: errorSchema,
      409: errorSchema,
      422: errorSchema,
    },
    summary: 'Create a new region in the authenticated user’s organization',
  },
  getRegion: {
    method: 'GET',
    path: '/regions/:regionId',
    pathParams: z.object({
      regionId: regionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: getRegionResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Get a region with its attached threads and rolled-up contributors',
  },
  updateRegion: {
    method: 'PATCH',
    path: '/regions/:regionId',
    pathParams: z.object({
      regionId: regionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: updateRegionRequestSchema,
    responses: {
      200: updateRegionResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
      422: errorSchema,
    },
    summary: 'Rename a region or update its blurb',
  },
  archiveRegion: {
    method: 'POST',
    path: '/regions/:regionId/archive',
    pathParams: z.object({
      regionId: regionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: archiveRegionRequestSchema,
    responses: {
      200: archiveRegionResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Archive or unarchive a region',
  },
  addRegionThread: {
    method: 'POST',
    path: '/regions/:regionId/threads',
    pathParams: z.object({
      regionId: regionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: addRegionThreadRequestSchema,
    responses: {
      201: addRegionThreadResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
      422: errorSchema,
    },
    summary:
      'Attach an existing visible thread to this region. Primary if the thread has no other primary, otherwise secondary (up to 2). Idempotent.',
  },
  suggestRegionsForThreads: {
    method: 'POST',
    path: '/regions/suggest-for-threads',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: suggestRegionsForThreadsRequestSchema,
    responses: {
      200: suggestRegionsForThreadsResponseSchema,
      401: errorSchema,
      403: errorSchema,
      422: errorSchema,
    },
    summary:
      'Batch lookup: for each thread id, return the highest-scoring active region in the viewer’s workspace (or omit when no region clears the threshold). Used by the per-thread inline "Add to <region>" suggestion chip.',
  },
  listUnfiledThreads: {
    method: 'GET',
    path: '/regions/unfiled',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: z.object({
      days: z.coerce.number().int().positive().max(180).optional(),
    }),
    responses: {
      200: listUnfiledThreadsResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary:
      'List threads in the authenticated user’s scope that are not yet attached to any region (default last 30 days).',
  },
  transitionRegionLifecycle: {
    method: 'POST',
    path: '/regions/lifecycle/transition',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: transitionRegionLifecycleResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary:
      'Sweep regions and demote/promote between active/quiet/dormant based on last_active_at. Idempotent — safe to run on a cron or on-demand.',
  },
  refreshRegions: {
    method: 'POST',
    path: '/regions/refresh',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: refreshRegionsResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary:
      'Run the clustering pipeline against unfiled threads and create regions from the resulting clusters. Deterministic pass is synchronous; LLM pass is dispatched as an async Inngest event and lands more regions ~30s later.',
  },
  synthesizeRegion: {
    method: 'POST',
    path: '/regions/:regionId/synthesize',
    pathParams: z.object({
      regionId: regionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: synthesizeRegionResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
      503: errorSchema,
    },
    summary:
      'Generate (or refresh) the region’s synthesized status using recent decisions and threads. Persists to regions.synthesized_status.',
  },
  removeRegionThread: {
    method: 'DELETE',
    path: '/regions/:regionId/threads/:threadId',
    pathParams: z.object({
      regionId: regionIdSchema,
      threadId: threadIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      204: z.object({}),
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Remove a thread membership from a region',
  },
});

export type RegionStatus = z.infer<typeof regionStatusSchema>;
export type RegionAuthor = z.infer<typeof regionAuthorSchema>;
export type RegionContributor = z.infer<typeof regionContributorSchema>;
export type RegionThread = z.infer<typeof regionThreadSchema>;
export type RegionDecisionKind = z.infer<typeof regionDecisionKindSchema>;
export type RegionDecision = z.infer<typeof regionDecisionSchema>;
export type RegionVelocity = z.infer<typeof regionVelocitySchema>;
export type RegionCitedBy = z.infer<typeof regionCitedBySchema>;
export type SynthesizeRegionResponse = z.infer<typeof synthesizeRegionResponseSchema>;
export type RegionListObject = z.infer<typeof regionListObjectSchema>;
export type RegionListResponse = z.infer<typeof regionListResponseSchema>;
export type ListRegionsQuery = z.infer<typeof listRegionsQuerySchema>;
export type CreateRegionRequest = z.infer<typeof createRegionRequestSchema>;
export type CreateRegionResponse = z.infer<typeof createRegionResponseSchema>;
export type UpdateRegionRequest = z.infer<typeof updateRegionRequestSchema>;
export type UpdateRegionResponse = z.infer<typeof updateRegionResponseSchema>;
export type ArchiveRegionRequest = z.infer<typeof archiveRegionRequestSchema>;
export type ArchiveRegionResponse = z.infer<typeof archiveRegionResponseSchema>;
export type GetRegionResponse = z.infer<typeof getRegionResponseSchema>;
export type AddRegionThreadRequest = z.infer<typeof addRegionThreadRequestSchema>;
export type AddRegionThreadResponse = z.infer<typeof addRegionThreadResponseSchema>;
export type RefreshRegionsResponse = z.infer<typeof refreshRegionsResponseSchema>;
export type TransitionRegionLifecycleResponse = z.infer<typeof transitionRegionLifecycleResponseSchema>;
export type UnfiledThread = z.infer<typeof unfiledThreadSchema>;
export type ListUnfiledThreadsResponse = z.infer<typeof listUnfiledThreadsResponseSchema>;
export type RegionSuggestion = z.infer<typeof regionSuggestionSchema>;
export type SuggestRegionsForThreadsRequest = z.infer<typeof suggestRegionsForThreadsRequestSchema>;
export type SuggestRegionsForThreadsResponse = z.infer<typeof suggestRegionsForThreadsResponseSchema>;
